import {useEffect, useState} from "react";
import {Button, Form, Modal, Spinner} from "react-bootstrap";
import ReactDatePicker from "react-datepicker";
import {Controller, useForm} from "react-hook-form";
import * as yup from "yup";
import {toast} from "react-toastify";
import {getApiErrorMessage, useYupValidationResolver} from "../../common";
import {ErrorMessage} from "../../parts/error-message";
import {
    DepartmentJobMapping,
    createDepartmentJobMapping,
    deleteDepartmentJobMapping,
    updateDepartmentJobMapping,
} from "../../api-service/department-job-mapping";
import {DeleteConfirmModal} from "./delete-confirm-modal";

const startOfDay = (date: Date): number => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
};


const normalizeToStartOfDay = (date: Date | null): Date | null => {
    if (!date) return null;
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
};

const trimString = (value: unknown) => (typeof value === "string" ? value.trim() : value);

const validationSchema = yup.object({
    JobCode: yup.string()
        .transform(trimString)
        .required("ジョブコードを入力してください")
        .max(30, "ジョブコードは30文字以内で入力してください"),
    JobName: yup.string()
        .transform(trimString)
        .required("ジョブ名を入力してください")
        .max(80, "ジョブ名は80文字以内で入力してください"),
    ParentJobCode: yup.string()
        .transform(trimString)
        .required("親ジョブコードを入力してください")
        .max(30, "親ジョブコードは30文字以内で入力してください"),
    ParentJobName: yup.string()
        .transform(trimString)
        .required("親ジョブ名を入力してください")
        .max(80, "親ジョブ名は80文字以内で入力してください"),
    DepartmentCode: yup.string()
        .transform(trimString)
        .required("部署コードを入力してください")
        .max(20, "部署コードは20文字以内で入力してください"),
    EffectiveStartDate: yup
        .date()
        .typeError("有効開始日を入力してください")
        .required("有効開始日を入力してください"),
    EffectiveEndDate: yup
        .date()
        .typeError("有効終了日を入力してください")
        .required("有効終了日を入力してください")
        .test(
            "end-date-not-before-start-date",
            "有効終了日は、有効開始日以降の日付を指定してください",
            function (value) {
                const {EffectiveStartDate} = this.parent;
                if (!value || !EffectiveStartDate) return true;
                return startOfDay(value) >= startOfDay(EffectiveStartDate);
            }
        ),
});

const toDate = (unixSeconds?: number) => (unixSeconds !== undefined && unixSeconds !== null ? new Date(unixSeconds * 1000) : null);
const toUnixSeconds = (date?: Date | null) => (date ? Math.floor(date.getTime() / 1000) : undefined);

const emptyFormValues = {
    JobCode: "",
    JobName: "",
    ParentJobCode: "",
    ParentJobName: "",
    EffectiveStartDate: null as Date | null,
    EffectiveEndDate: null as Date | null,
    DepartmentCode: "",
};

interface JobMappingFormModalProps {
    show: boolean;
    mode: "create" | "edit";
    initialData: DepartmentJobMapping | null;
    onClose: () => void;
    onSaved: () => void;
    onDeleted: () => void;
}

export function JobMappingFormModal({show, mode, initialData, onClose, onSaved, onDeleted}: JobMappingFormModalProps) {
    const resolver = useYupValidationResolver(validationSchema);
    const [isSaving, setIsSaving] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const {
        register,
        control,
        handleSubmit,
        reset,
        formState: {errors, isSubmitted},
    } = useForm({
        resolver,
        defaultValues: emptyFormValues,
        mode: "onBlur",
        reValidateMode: "onChange",
    });

    useEffect(() => {
        if (!show) return;
        if (mode === "edit" && initialData) {
            reset({
                JobCode: initialData.JobCode,
                JobName: initialData.JobName || "",
                ParentJobCode: initialData.ParentJobCode || "",
                ParentJobName: initialData.ParentJobName || "",
                EffectiveStartDate: toDate(initialData.EffectiveStartDate),
                EffectiveEndDate: toDate(initialData.EffectiveEndDate),
                DepartmentCode: initialData.DepartmentCode || "",
            });
        } else {
            reset(emptyFormValues);
        }
    }, [show, mode, initialData, reset]);

    const submitFormData = async (data: typeof emptyFormValues) => {
        setIsSaving(true);
        const payload = {
            JobCode: data.JobCode,
            JobName: data.JobName,
            ParentJobCode: data.ParentJobCode,
            ParentJobName: data.ParentJobName,
            EffectiveStartDate: toUnixSeconds(data.EffectiveStartDate) as number,
            EffectiveEndDate: toUnixSeconds(data.EffectiveEndDate),
            DepartmentCode: data.DepartmentCode,
        };
        try {
            if (mode === "edit" && initialData) {
                await updateDepartmentJobMapping(initialData.JobCode, initialData.DepartmentCode || "", payload);
                toast.success("更新しました。");
            } else {
                await createDepartmentJobMapping(payload);
                toast.success("登録しました。");
            }
            onSaved();
        } catch (error: any) {
            const fallback = mode === "edit" ? "ジョブ編集に失敗しました。再度お試しください。" : "ジョブ登録に失敗しました。再度お試しください。";
            toast.error(getApiErrorMessage(error, fallback));
        } finally {
            setIsSaving(false);
        }
    };

    const handleConfirmDelete = async () => {
        if (!initialData) return;
        try {
            await deleteDepartmentJobMapping(initialData.JobCode, initialData.DepartmentCode || "");
            toast.success("解除しました。");
            setShowDeleteConfirm(false);
            onDeleted();
        } catch (error: any) {
            toast.error(getApiErrorMessage(error, "ジョブ削除に失敗しました。再度お試しください。"));
        }
    };

    return (
        <Modal className="job-mapping-modal pt-5" show={show} onHide={onClose}>
            <Form onSubmit={handleSubmit(submitFormData)}>
                <Modal.Header closeButton>
                    {mode === "edit" ? "ジョブ編集" : "ジョブ新規作成"}
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex pt-3">
                        <Form.Label className="mt-2 me-2 modal-form-label fs-12">ジョブコード</Form.Label>
                        <div className="d-flex w-100">
                            <Form.Control className="modal-form-control" size="sm" {...register("JobCode")}/>
                        </div>
                    </div>
                    <div className="d-flex">
                        <Form.Label className="modal-form-label fs-12"></Form.Label>
                        <div className="d-flex w-100 ms-2">
                            {isSubmitted && <ErrorMessage name="JobCode" className="px-0 pt-1 mb-0" errors={errors}/>}
                        </div>
                    </div>
                    <div className="d-flex pt-3">
                        <Form.Label className="mt-2 me-2 modal-form-label fs-12">ジョブ名</Form.Label>
                        <div className="d-flex w-100">
                            <Form.Control className="modal-form-control" size="sm" {...register("JobName")}/>
                        </div>
                    </div>
                    <div className="d-flex">
                        <Form.Label className="modal-form-label fs-12"></Form.Label>
                        <div className="d-flex w-100 ms-2">
                            {isSubmitted && <ErrorMessage name="JobName" className="px-0 pt-1 mb-0" errors={errors}/>}
                        </div>
                    </div>
                    <div className="d-flex pt-3">
                        <Form.Label className="mt-2 me-2 modal-form-label fs-12">親ジョブコード</Form.Label>
                        <div className="d-flex w-100">
                            <Form.Control className="modal-form-control" size="sm" {...register("ParentJobCode")}/>
                        </div>
                    </div>
                    <div className="d-flex">
                        <Form.Label className="modal-form-label fs-12"></Form.Label>
                        <div className="d-flex w-100 ms-2">
                            {isSubmitted && <ErrorMessage name="ParentJobCode" className="px-0 pt-1 mb-0" errors={errors}/>}
                        </div>
                    </div>
                    <div className="d-flex pt-3">
                        <Form.Label className="mt-2 me-2 modal-form-label fs-12">親ジョブ名</Form.Label>
                        <div className="d-flex w-100">
                            <Form.Control className="modal-form-control" size="sm" {...register("ParentJobName")}/>
                        </div>
                    </div>
                    <div className="d-flex">
                        <Form.Label className="modal-form-label fs-12"></Form.Label>
                        <div className="d-flex w-100 ms-2">
                            {isSubmitted && <ErrorMessage name="ParentJobName" className="px-0 pt-1 mb-0" errors={errors}/>}
                        </div>
                    </div>
                    <div className="d-flex pt-3">
                        <Form.Label className="mt-2 me-2 modal-form-label fs-12">有効開始日</Form.Label>
                        <div className="d-block w-60">
                            <Controller
                                control={control}
                                name="EffectiveStartDate"
                                render={({field}) => (
                                    <ReactDatePicker
                                        selected={field.value}
                                        onChange={(date) => field.onChange(normalizeToStartOfDay(date))}
                                        dateFormat="yyyy/MM/dd"
                                        className="form-control modal-form-control calendar-icon"
                                    />
                                )}
                            />
                            {isSubmitted &&
                                <ErrorMessage name="EffectiveStartDate" className="px-0 pt-1 mb-0" errors={errors}/>}
                        </div>
                    </div>
                    <div className="d-flex pt-3">
                        <Form.Label className="mt-2 me-2 modal-form-label">有効終了日</Form.Label>
                        <div className="d-block w-60">
                            <Controller
                                control={control}
                                name="EffectiveEndDate"
                                render={({field}) => (
                                    <ReactDatePicker
                                        selected={field.value}
                                        onChange={(date) => field.onChange(normalizeToStartOfDay(date))}
                                        dateFormat="yyyy/MM/dd"
                                        className="form-control modal-form-control calendar-icon"
                                    />
                                )}
                            />
                            {isSubmitted &&
                                <ErrorMessage name="EffectiveEndDate" className="px-0 pt-1 mb-0" errors={errors}/>}
                        </div>
                    </div>
                    <div className="d-flex pt-3">
                        <Form.Label className="mt-2 me-2 modal-form-label">部署コード</Form.Label>
                        <div className="d-block w-50">
                            <Form.Control className="modal-form-control" size="sm" {...register("DepartmentCode")}/>

                        </div>
                    </div>
                    <div className="d-flex">
                        <Form.Label className=" me-2 modal-form-label"></Form.Label>
                        <div className="d-block w-100">
                            {isSubmitted &&
                                <ErrorMessage name="DepartmentCode" className="px-0 pt-1 mb-0" errors={errors}/>}

                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="modal-footer">
                    {mode === "edit" && (
                        <Button
                            variant="outline-danger"
                            size="sm"
                            className="bg-white modal-btn me-auto btn-delete-mapping"
                            onClick={() => setShowDeleteConfirm(true)}
                        >
                            削除
                        </Button>
                    )}
                    <Button variant="light"  size="sm" className="modal-btn modal-cancel-btn" onClick={onClose}>
                        キャンセル
                    </Button>
                    <Button variant="primary" style={{background:"#0176D3"}} size="sm" type="submit" className="modal-btn btn-save-common" disabled={isSaving}>
                        {isSaving ? (
                            <>
                                <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true"/>
                                保存...
                            </>
                        ) : (
                            "保存"
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
            <DeleteConfirmModal
                show={showDeleteConfirm}
                onCancel={() => setShowDeleteConfirm(false)}
                onConfirm={handleConfirmDelete}
            />
        </Modal>
    );
}
